/**
 * 7 - Disable a SAM Site (03 §2): 1-4 launchers (one per two points of threat), guarded by
 * automated mini-bunkers whose live/dormant cycle is the puzzle. The manual's strict quiet
 * rule: knife only until inside the goal zone - no-contact pays, a shot outside it costs.
 */

import { placeMiniBunker } from '../garrison';
import type { Point } from '../types';
import { checker, clutter, guardsAbout, required, ruleOf, structure, targetsOf } from './helpers';
import type { ArchetypeSpec } from './types';

export const launchersFor = (threat: number): number => Math.min(4, Math.max(1, Math.ceil(threat / 2)));
const bunkersFor = (threat: number): number => 2 + (threat >= 6 ? 1 : 0) + (threat >= 8 ? 1 : 0);

export const samSite: ArchetypeSpec = {
  id: 'sam-site',
  name: 'DISABLE A SAM SITE',
  nativeBiome: 'desert',
  baseThreat: 5,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [48, 58], d: [30, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const placed: Point[] = [];
    const ids: number[] = [];
    for (let i = 0; i < launchersFor(g.threat); i++) {
      ids.push(required(g, 'samLauncher', { target: true, minDist: 12, from: placed, margin: 1.4 }).id);
    }
    const launchers = [...placed];
    structure(g, 'hut', { minDist: 8, from: placed });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);

    // Mini-bunkers stand between the launchers and the gates: whoever moves faster than a crawl is shot.
    const wanted = bunkersFor(g.threat);
    let sited = 0;
    for (let tries = 0; tries < 80 && sited < wanted; tries++) {
      const by = launchers[tries % Math.max(1, launchers.length)];
      if (!by) break;
      const x = by.x + g.rng.range(-9, 9);
      // South of the launcher for choice; anywhere round it once that has been tried enough.
      const z = by.z + (tries < 40 ? g.rng.range(4, 10) : g.rng.range(-9, 10));
      const inCompound = z <= g.inner.z1 && z >= g.inner.z0 && x >= g.inner.x0 && x <= g.inner.x1;
      if (inCompound && placeMiniBunker(g.map, x, z, 1)) sited++;
    }
    if (sited < 2) g.map.problems.push('sam-site: no room for the mini-bunkers');
    guardsAbout(g, 2);
    const zone = { ...g.rect };
    return {
      objective: {
        kind: 'destroyTargets', label: 'DISABLE SAM SITE', targetPropIds: ids, minFraction: 1,
        rules: [
          { type: 'quietUntilZone', zone, breach: 'penalty' },
          { type: 'noContactBonus' },
          { type: 'recall', mode: 'normal' },
        ],
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('sam-site');
    const launchers = targetsOf(def, 'samLauncher');
    check(
      launchers.length === launchersFor(def.threat),
      `needs ${launchersFor(def.threat)} launcher(s) at threat ${def.threat}, has ${launchers.length}`,
    );
    const bunkers = def.spawns.filter((s) => s.kind === 'miniBunker');
    check(bunkers.length >= 2, 'needs at least two automated mini-bunkers');
    check(bunkers.every((b) => b.dormantUntil === undefined), 'mini-bunkers are there from the start');
    check(ruleOf(def, 'quietUntilZone')?.breach === 'penalty', 'needs the strict quiet rule with a penalty');
    check(ruleOf(def, 'noContactBonus') !== undefined, 'offers the no-contact bonus');
    return out;
  },
};
